import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

function MultiSelectAutocomplete({
    options = [],
    value = [],
    onChange,
    label,
    placeholder,
    disabled = false,
    error,
    getOptionLabel,
    isOptionEqualToValue,
}) {
    const { t } = useTranslation('validation');

    let errorMessage = null;
    if (error) {
        if (typeof error === 'object' && error.code) {
            if (error.params) {
                const entries = Object.entries(error?.params ?? {});
                const errorValue = entries.length > 0 ? entries[0][1] : undefined;
                errorMessage = t(error.code, { toReplace: errorValue });
            } else {
                errorMessage = t(error.code);
            }
        } else if (typeof error === 'string') {
            errorMessage = error;
        }
    }

    const defaultGetOptionLabel = (option) =>
        option?.label ?? option?.name ?? option?.code ?? String(option ?? '');

    const defaultIsOptionEqualToValue = (option, selected) =>
        (option?.id && selected?.id && option.id === selected.id) ||
        (option?.code && selected?.code && option.code === selected.code) ||
        option === selected;

    return (
        <Autocomplete
            multiple
            options={options}
            value={value}
            onChange={(_, selectedValues) => onChange?.(selectedValues)}
            getOptionLabel={getOptionLabel ?? defaultGetOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue ?? defaultIsOptionEqualToValue}
            disableCloseOnSelect
            disabled={disabled}
            size="small"
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: 'var(--box-background-color)',
                        color: 'var(--text-color)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        '& .MuiAutocomplete-option:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                    },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={Boolean(error)}
                    helperText={errorMessage}
                />
            )}
            sx={{
                width: '100%',
                marginBottom: '16px',
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'var(--text-color)',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--button-primary)',
                        borderWidth: '1px',
                    },
                    '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(84, 112, 153, 0.1)',
                    }
                },
                '&:hover .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(82, 128, 199, 0.12)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    transition: 'border-color 0.2s ease',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.6)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--button-primary)',
                },
                '& .MuiChip-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiSvgIcon-root': {
                    color: 'var(--text-color)',
                }
            }}
        />
    );
}

export default MultiSelectAutocomplete;
